import { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { BrandLogo } from '@/components/ui/BrandLogo'

export function Header() {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  // Check if we're on a page that should show back button (archive pages + detail pages)
  const shouldShowBackButton =
    location.pathname.match(/^\/moves(\/[^/]+)?$/) || // /moves or /moves/123
    location.pathname.match(/^\/series(\/[^/]+)?$/) || // /series or /series/123
    location.pathname.match(/^\/tags(\/[^/]+)?$/) // /tags or /tags/123

  const handleLogout = async () => {
    await logout()
    navigate('/', { replace: true })
  }

  const handleBack = () => {
    // Smart back navigation - avoid error loops
    // If we're on a detail page, navigate to the parent archive instead of browser back
    const currentPath = location.pathname

    // Detail page patterns: /moves/:id, /series/:id, /tags/:id
    if (currentPath.match(/^\/moves\/[^/]+$/)) {
      navigate('/moves', { replace: true })
    } else if (currentPath.match(/^\/series\/[^/]+$/)) {
      navigate('/series', { replace: true })
    } else if (currentPath.match(/^\/tags\/[^/]+$/)) {
      navigate('/tags', { replace: true })
    } else {
      // For archive pages, go to dashboard
      navigate('/dashboard', { replace: true })
    }
  }

  const closeDrawer = () => {
    setIsDrawerOpen(false)
  }

  return (
    <>
      <header className="sticky top-0 z-50 border-b border-gray-200 bg-white shadow-sm">
        <div className="mx-auto max-w-6xl px-4">
          <div className="flex h-16 items-center justify-between">
            {/* Left side - Back button */}
            <div className="flex w-20 items-center">
              {shouldShowBackButton ? (
                <button
                  onClick={handleBack}
                  className="flex items-center text-blue-600 hover:text-blue-700"
                >
                  <svg
                    className="mr-1 h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                  Back
                </button>
              ) : null}
            </div>

            {/* Center - Brand */}
            <div className="flex flex-1 justify-center">
              <Link to="/dashboard" className="flex items-center">
                <BrandLogo size="header" />
              </Link>
            </div>

            {/* Right side - Hamburger menu */}
            <div className="flex w-20 items-center justify-end">
              <button
                onClick={() => setIsDrawerOpen(true)}
                className="flex h-8 w-8 items-center justify-center text-gray-600 hover:text-gray-900"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Drawer Overlay */}
      {isDrawerOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          {/* Background overlay */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={closeDrawer}
          />

          {/* Drawer panel */}
          <div className="absolute right-0 top-0 h-full w-80 max-w-full bg-white shadow-xl">
            <div className="flex h-full flex-col">
              {/* Drawer header */}
              <div className="border-b border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">Menu</h2>
                  <button
                    onClick={closeDrawer}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              {/* User info section */}
              <div className="border-b border-gray-200 p-4">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-900">
                    {user?.firstName || 'User'} {user?.lastName || ''}
                  </p>
                  {user?.belt && (
                    <div className="w-[90%]">
                      {/* Compact Belt Display */}
                      <div
                        className="relative h-12 w-full overflow-hidden rounded-md shadow-md"
                        style={{
                          backgroundColor:
                            user.belt === 'WHITE'
                              ? '#FFFFFF'
                              : user.belt === 'BLUE'
                                ? '#0033A0'
                                : user.belt === 'PURPLE'
                                  ? '#6B3FA0'
                                  : user.belt === 'BROWN'
                                    ? '#8B4513'
                                    : '#000000',
                          border: `2px solid ${
                            user.belt === 'WHITE'
                              ? '#E5E5E5'
                              : user.belt === 'BLUE'
                                ? '#002080'
                                : user.belt === 'PURPLE'
                                  ? '#552E80'
                                  : user.belt === 'BROWN'
                                    ? '#6B3410'
                                    : '#000000'
                          }`,
                        }}
                      >
                        {/* Stripes */}
                        {user?.rank &&
                          user.rank !== '0' &&
                          parseInt(user.rank, 10) > 0 && (
                            <div className="absolute inset-y-0 right-0 flex items-center gap-1.5 pr-3">
                              {Array.from({
                                length: parseInt(user.rank, 10),
                              }).map((_, i) => (
                                <div
                                  key={i}
                                  className="h-full w-2.5"
                                  style={{
                                    backgroundColor: '#FFFFFF',
                                    border: '1px solid rgba(0, 0, 0, 0.3)',
                                    boxShadow:
                                      '0 1px 3px rgba(0, 0, 0, 0.4), inset 0 1px 1px rgba(255, 255, 255, 0.5)',
                                  }}
                                />
                              ))}
                            </div>
                          )}
                      </div>
                      <p className="mt-1 text-xs text-gray-600">
                        {user.belt.charAt(0) + user.belt.slice(1).toLowerCase()}{' '}
                        Belt
                        {user?.rank &&
                        user.rank !== '0' &&
                        parseInt(user.rank, 10) > 0
                          ? ` • ${user.rank} ${parseInt(user.rank, 10) === 1 ? 'Stripe' : 'Stripes'}`
                          : ''}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Navigation items */}
              <div className="flex-1 p-4">
                <nav className="space-y-2">
                  <Link
                    to="/account"
                    onClick={closeDrawer}
                    className="flex items-center rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                  >
                    <svg
                      className="mr-3 h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                    Account
                  </Link>

                  <Link
                    to="/moves"
                    onClick={closeDrawer}
                    className="flex items-center rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                  >
                    <svg
                      className="mr-3 h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Moves
                  </Link>

                  <Link
                    to="/series"
                    onClick={closeDrawer}
                    className="flex items-center rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                  >
                    <svg
                      className="mr-3 h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                      />
                    </svg>
                    Series
                  </Link>

                  <Link
                    to="/tags"
                    onClick={closeDrawer}
                    className="flex items-center rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                  >
                    <svg
                      className="mr-3 h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                      />
                    </svg>
                    Tags
                  </Link>
                </nav>
              </div>

              {/* Drawer footer */}
              <div className="border-t border-gray-200 p-4">
                <button
                  onClick={handleLogout}
                  className="flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
                >
                  <svg
                    className="mr-3 h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
